import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";
import {
  useTaxDocuments,
  TaxDocumentFilters,
  TaxDocumentTable,
  PendingActionsBanner,
  SubmitToSiiDialog,
} from "@/features/tax-documents";
import type { TaxDocumentsFilters } from "@/features/tax-documents/hooks/useTaxDocuments";

export function DocumentosPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TaxDocumentsFilters>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  const { documents, total, isLoading, error, refetch } = useTaxDocuments({
    filters,
    limit: 50,
    offset: 0,
  });

  // Count pending documents
  const pendingCount = useMemo(
    () => documents.filter((d) => d.status === "pending").length,
    [documents],
  );

  // Get selected documents for submit dialog
  const selectedDocuments = useMemo(
    () =>
      documents.filter((d) => selectedIds.has(d.id) && d.status === "pending"),
    [documents, selectedIds],
  );

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleDownloadXml = async (id: string) => {
    try {
      const blob = await apiFetch.blob(`/api/tax-documents/${id}/xml`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-${id}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Error al descargar el XML");
    }
  };

  const handleDownloadPdf = async (id: string) => {
    try {
      const blob = await apiFetch.blob(`/api/tax-documents/${id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Error al generar el PDF");
    }
  };

  const handleSubmitPending = () => {
    // Select all pending documents
    const pendingIds = new Set(
      documents.filter((d) => d.status === "pending").map((d) => d.id),
    );
    setSelectedIds(pendingIds);
    setSubmitDialogOpen(true);
  };

  const handleSubmitSuccess = () => {
    setSelectedIds(new Set());
    refetch();
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Documentos Tributarios</h1>
          <p className="text-muted-foreground">
            Gestiona tus facturas, notas de crédito y otros DTEs
          </p>
        </div>
        <Button onClick={() => navigate("/facturacion/emision/nuevo")}>
          <Plus className="mr-2 h-4 w-4" />
          Emitir Documento
        </Button>
      </div>

      {/* Filters */}
      <TaxDocumentFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Pending Banner */}
      <PendingActionsBanner
        pendingCount={pendingCount}
        onSubmitClick={handleSubmitPending}
      />

      {/* Selected Actions */}
      {selectedIds.size > 0 && selectedDocuments.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-primary/5 rounded-lg">
          <span className="text-sm">
            {selectedDocuments.length} documento(s) seleccionado(s)
          </span>
          <Button size="sm" onClick={() => setSubmitDialogOpen(true)}>
            Enviar a SII
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Cancelar
          </Button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Cargando documentos...
        </div>
      ) : (
        <TaxDocumentTable
          documents={documents}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onDownloadXml={handleDownloadXml}
          onDownloadPdf={handleDownloadPdf}
        />
      )}

      {/* Pagination info */}
      {!isLoading && documents.length > 0 && (
        <div className="mt-4 text-sm text-muted-foreground">
          Mostrando {documents.length} de {total} documentos
        </div>
      )}

      {/* Submit Dialog */}
      <SubmitToSiiDialog
        open={submitDialogOpen}
        onOpenChange={setSubmitDialogOpen}
        documents={selectedDocuments}
        onSuccess={handleSubmitSuccess}
      />
    </div>
  );
}
